<template>
  <div
    ref="rootEl"
    class="wui-virtual-scroller"
    :class="{ 'wui-virtual-scroller--page-mode': pageMode }"
    v-bind="$attrs"
  >
    <virtualizer
      v-if="!pageMode || pageScrollEl"
      ref="listRef"
      v-bind="virtuaBind"
      :item-props="resolveItemProps"
      @scroll="onVirtuaScroll"
      @scroll-end="onVirtuaScrollEnd"
    >
      <template #default="{ item, index }">
        <!--
          Single keyed root is required: virtua falls back to "_"+index when key is missing,
          which breaks size cache after prepend (blank/hidden items).
        -->
        <div
          :key="resolveKey(item, index)"
          class="wui-virtual-scroller__item"
          :class="{ 'wui-virtual-scroller__item--last': index === items.length - 1 }"
          :data-vs-key="String(resolveKey(item, index))"
          :data-index="index"
          :style="itemShellStyle"
        >
          <slot
            :item="item"
            :index="index"
            :active="true"
          />
        </div>
      </template>
    </virtualizer>
  </div>
</template>

<script lang="ts" setup generic="T">
import { Virtualizer, type VirtualizerHandle } from 'virtua/vue'
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from 'vue'

import type { ScrollToItemOptions } from '../lib/types'

defineOptions({ inheritAttrs: false })

type ItemKey = string | number

type VirtuaBind = {
  data: T[]
  bufferSize: number
  shift: boolean
  itemSize?: number
  ssrCount?: number
  keepMounted?: readonly number[]
  scrollRef?: HTMLElement
  startMargin?: number
}

/* eslint-disable vue/require-default-prop -- optional props omitted for exactOptionalPropertyTypes */
const props = withDefaults(defineProps<{
  items: T[]
  /** Stable id field — never use array index with shift/prepend (virtua FAQ). */
  keyField?: string
  /**
   * Initial size hint for unmeasured items.
   * When set, virtua applies bufferSize from the start (auto-estimate mode skips buffer).
   * Prefer a short-bubble median (~40), not an overestimate — too high leaves blank gaps.
   */
  itemSize?: number
  /** Maps to virtua bufferSize (px). Default docs=200; chat uses a larger value to avoid blanks. */
  buffer?: number
  pageMode?: boolean
  /**
   * Optional explicit shift. Prefer auto-detect: VirtualScroller sets shift for the
   * prepend length-change frame when the first item key changes and the last stays.
   * Auto-shift is disabled in pageMode (top-anchored lists like ChatList).
   */
  shift?: boolean
  keepMounted?: number[]
  /** SSR hydrate count → virtua ssrCount. */
  ssrCount?: number
  /**
   * Offset before this virtualizer inside the scroll parent (virtua page / window mode).
   * When omitted in pageMode, measured automatically from DOM.
   */
  startMargin?: number
  /**
   * virtua hides unmeasured rows (visibility:hidden). For chat that reads as blank
   * gaps on first scroll — keep content visible and rely on itemSize/minHeight.
   */
  revealUnmeasured?: boolean
}>(), {
  keyField: 'id',
  buffer: 1200,
  pageMode: false,
  shift: false,
  ssrCount: 0,
  revealUnmeasured: false,
})

const emit = defineEmits<{
  resize: []
  scroll: [event: Event]
  'scroll-end': []
}>()

const rootEl = ref<HTMLElement | null>(null)
const pageScrollEl = ref<HTMLElement | null>(null)
const listRef = ref<VirtualizerHandle | null>(null)
const measuredStartMargin = ref(0)
let startMarginObserver: ResizeObserver | undefined

/**
 * Auto shift for the same render as a top-prepend (virtua Chat / issue #284 pattern).
 * flush:'sync' so length watch inside Virtualizer sees shift=true on that frame.
 * Skipped in pageMode — ChatList is top-anchored; shift-from-end creates top gaps.
 */
const autoShift = ref(false)
let prevFirstKey: ItemKey | null = null
let prevLastKey: ItemKey | null = null
let prevLength = 0

const resolveKey = (item: T, index: number): ItemKey => {
  const record = item as Record<string, unknown>
  const value = record[props.keyField] ?? record.id ?? record.eventId
  if (value === undefined || value === null || value === '') {
    return `missing-key:${index}`
  }
  return value as ItemKey
}

watch(
  () => props.items,
  (items) => {
    if (props.pageMode) {
      autoShift.value = false
      const list = items ?? []
      prevLength = list.length
      prevFirstKey = prevLength ? resolveKey(list[0] as T, 0) : null
      prevLastKey = prevLength ? resolveKey(list[prevLength - 1] as T, prevLength - 1) : null
      return
    }

    const list = items ?? []
    const length = list.length
    const firstKey = length ? resolveKey(list[0] as T, 0) : null
    const lastKey = length ? resolveKey(list[length - 1] as T, length - 1) : null

    autoShift.value = prevLength > 0
      && length > prevLength
      && firstKey !== null
      && lastKey !== null
      && firstKey !== prevFirstKey
      && lastKey === prevLastKey

    prevFirstKey = firstKey
    prevLastKey = lastKey
    prevLength = length
  },
  { flush: 'sync', immediate: true },
)

watch(autoShift, (enabled) => {
  if (!enabled) {
    return
  }
  // Drop after commit — keep false for mid/end appends and remasures.
  void nextTick(() => {
    autoShift.value = false
  })
})

const effectiveShift = computed(() => props.shift || autoShift.value)

const findScrollableParent = (from: HTMLElement | null): HTMLElement | null => {
  let node = from?.parentElement ?? null
  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return node
    }
    node = node.parentElement
  }
  return null
}

/** Distance from scroll-content top to this virtualizer — required by virtua when siblings precede it. */
const measureStartMargin = (): void => {
  if (!props.pageMode || props.startMargin != null) {
    return
  }
  const root = rootEl.value
  const scroll = pageScrollEl.value
  if (!root || !scroll) {
    return
  }
  const next = Math.max(
    0,
    Math.round(root.getBoundingClientRect().top - scroll.getBoundingClientRect().top + scroll.scrollTop),
  )
  if (next !== measuredStartMargin.value) {
    measuredStartMargin.value = next
  }
}

const bindStartMarginObserver = (): void => {
  startMarginObserver?.disconnect()
  startMarginObserver = undefined
  if (!props.pageMode || props.startMargin != null) {
    return
  }
  const scroll = pageScrollEl.value
  const root = rootEl.value
  if (!scroll || !root || typeof ResizeObserver === 'undefined') {
    measureStartMargin()
    return
  }
  startMarginObserver = new ResizeObserver(() => {
    measureStartMargin()
  })
  startMarginObserver.observe(scroll)
  startMarginObserver.observe(root)
  if (root.parentElement) {
    startMarginObserver.observe(root.parentElement)
  }
  measureStartMargin()
}

const resolvedStartMargin = computed(() => {
  if (props.startMargin != null) {
    return props.startMargin
  }
  return measuredStartMargin.value
})

const virtuaBind = computed((): VirtuaBind => {
  const bind: VirtuaBind = {
    data: props.items,
    bufferSize: props.buffer,
    shift: effectiveShift.value,
  }
  if (props.itemSize != null) {
    bind.itemSize = props.itemSize
  }
  if (props.ssrCount > 0) {
    bind.ssrCount = props.ssrCount
  }
  if (props.keepMounted?.length) {
    bind.keepMounted = props.keepMounted
  }
  if (props.pageMode && pageScrollEl.value) {
    bind.scrollRef = pageScrollEl.value
    bind.startMargin = resolvedStartMargin.value
  }
  return bind
})

/** Reserve space while virtua keeps unmeasured rows `visibility: hidden`. */
const itemShellStyle = computed(() => {
  if (props.itemSize == null || props.itemSize <= 0) {
    return undefined
  }
  return { minHeight: `${props.itemSize}px` }
})

const resolveItemProps = ({ item, index }: { item: T; index: number }) => ({
  'data-vs-key': String(resolveKey(item, index)),
  'data-index': String(index),
  // Merged after virtua's visibility:hidden — show content before first measure.
  ...(props.revealUnmeasured ? { style: { visibility: 'visible' as const }} : {}),
})

const getScrollElement = (): HTMLElement | null => {
  if (props.pageMode) {
    return pageScrollEl.value ?? findScrollableParent(rootEl.value)
  }
  return rootEl.value
}

const getItemOffset = (index: number): number => {
  return listRef.value?.getItemOffset(index) ?? 0
}

const scrollToItem = (index: number, options?: ScrollToItemOptions): void => {
  if (index < 0 || index >= props.items.length) {
    return
  }
  const align = options?.align === 'auto' ? 'nearest' : (options?.align ?? 'start')
  listRef.value?.scrollToIndex(index, { align })
  void nextTick(() => {
    listRef.value?.scrollToIndex(index, { align })
  })
}

const scrollToBottom = (): void => {
  const handle = listRef.value
  const last = props.items.length - 1
  if (!handle || last < 0) {
    return
  }
  handle.scrollToIndex(last, { align: 'end' })
  void nextTick(() => {
    const settled = listRef.value
    if (!settled) {
      return
    }
    const offset = Math.max(0, settled.scrollSize - settled.viewportSize)
    settled.scrollTo(offset)
    settled.scrollToIndex(last, { align: 'end' })
  })
}

const scrollTo = (offset: number): void => {
  listRef.value?.scrollTo(offset)
}

const onVirtuaScroll = (_offset: number): void => {
  void _offset
  emit('scroll', new Event('scroll'))
}

const onVirtuaScrollEnd = (): void => {
  emit('scroll-end')
  emit('resize')
}

onMounted(() => {
  if (props.pageMode) {
    pageScrollEl.value = findScrollableParent(rootEl.value)
    bindStartMarginObserver()
  }
  nextTick(() => {
    measureStartMargin()
    emit('resize')
  })
})

onBeforeUnmount(() => {
  startMarginObserver?.disconnect()
  startMarginObserver = undefined
})

watch(
  () => [props.pageMode, props.startMargin, props.items.length] as const,
  () => {
    if (!props.pageMode) {
      return
    }
    void nextTick(() => {
      if (!pageScrollEl.value) {
        pageScrollEl.value = findScrollableParent(rootEl.value)
      }
      bindStartMarginObserver()
    })
  },
)

defineExpose({
  scrollToItem,
  scrollToBottom,
  scrollTo,
  getItemOffset,
  get $el () {
    return getScrollElement()
  },
  get scrollOffset () {
    return listRef.value?.scrollOffset ?? 0
  },
  get scrollSize () {
    return listRef.value?.scrollSize ?? 0
  },
  get viewportSize () {
    return listRef.value?.viewportSize ?? 0
  },
})
</script>

<style scoped>
.wui-virtual-scroller {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  overflow-anchor: none;
  scrollbar-gutter: stable;
}

.wui-virtual-scroller--page-mode {
  height: auto;
  overflow: visible;
  scrollbar-gutter: auto;
}

.wui-virtual-scroller__item {
  width: 100%;
  box-sizing: border-box;
}
</style>
